/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/pdfInvoiceTable.scss';

import React, {MouseEventHandler} from 'react';
import {
    ElementPosition,
    PDFElementParams, PDFElementPersist, PDFElementType, PdfFont, PDFInvoiceTableParams
} from "../../util/interfaces";
import {PDFDraggable, PdfElement} from "../element/pdfElement";
import {parsePrice, pxToPt} from "../../util/pdfUtil";
import {WithEvents} from "../../../../index";

interface Props {
    onSaveHistory: VoidFunction;
    onDelete: MouseEventHandler;
    defaultSettings?: Partial<PDFInvoiceTableParams>;
    id?: string;
    defaultPosition: { x: number; y: number; };
    draggable: boolean;
    onFocusElement: (element: PdfElement) => void;
    width?: number;
    height?: number;
    persistent?: PDFElementPersist;
    mainFont: PdfFont;
    tableHeaderLabels?: [string, string, string, string, string];
}

interface State {
    settings: PDFInvoiceTableParams;
    dragging: boolean;
}

export default class PdfInvoiceTable extends PdfElement<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            settings: {
                widths: [40, 20, 20, 20],
                fontSize: 12,
                font: props.mainFont,
                color: '#000',
                headerColor: '#000',
                alignment: ElementPosition.CENTER,
                persistent: PDFElementPersist.NOT_PERSISTENT,
                ...props.defaultSettings
            },
            dragging: false,
        };
    }

    getParams = (convertPixels: boolean = true): PDFElementParams<PDFElementType.INVOICE_TABLE, PDFInvoiceTableParams> => {
        const {settings} = this.state, fontSize = convertPixels ? pxToPt(settings.fontSize) : settings.fontSize;
        return {
            dimensions: this.getDimensions(convertPixels),
            type: PDFElementType.INVOICE_TABLE,
            key: this.id,
            options: {
                widths: settings.widths,
                fontSize,
                font: settings.font,
                color: settings.color,
                headerColor: settings.headerColor,
                alignment: settings.alignment,
                persistent: settings.persistent,
            },
        };
    }

    onClick = (evt: React.MouseEvent): void => {
        evt.stopPropagation();
        this.props.onFocusElement(this);
        if (this.draggable)
            this.draggable.setFocus();
    };

    onClickDelete = (): void => {
        // Deleting invoice table is not allowed
        return;
    };

    onClickBar = (evt: React.MouseEvent) => {
        this.onClick(evt);
    };

    private getRowStyle = (isHeadRow: boolean = false): React.CSSProperties => {
        const {widths, fontSize, font, color, headerColor} = this.state.settings,
            style: React.CSSProperties = {fontSize, fontFamily: font, color};
        if (isHeadRow) {
            style.fontWeight = font === PdfFont.HELVETICA ? 600 : 500;
            style.borderBottom = '2px solid #d8d8d8';
            style.color = headerColor;
        }
        style.gridTemplateColumns = widths.join('% ') + '%';
        return style;
    }

    renderInvoiceLine = (index: number) => {
        const amount = index === 1 ? 2 : 14, piecePrice = index === 1 ? 120 : 175, price = index === 1 ? 240 : 2450;
        return (
            <div className='invoice-line' key={index} style={this.getRowStyle()}>
                <span>Productnaam {index}</span>
                <span>{amount}</span>
                <span>&euro; {parsePrice(piecePrice)}</span>
                <span>21%</span>
                <span className='end'>&euro; {parsePrice(price)}</span>
            </div>
        )
    };

    renderTotals = () => {
        const {font} = this.state.settings;
        return (
            <>
                <div className='invoice-line mt bt' style={this.getRowStyle()}>
                    <span/>
                    <span/>
                    <span className='light fl'>Totaal excl. BTW</span>
                    <span/>
                    <span className='end fl'>&euro; {'{subtotaal}'}</span>
                </div>
                <div className='invoice-line' style={this.getRowStyle()}>
                    <span/>
                    <span/>
                    <span className='light fl'>BTW (21%)</span>
                    <span/>
                    <span className='end fl'>&euro; {'{btw_totaal}'}</span>
                </div>
                <div className='invoice-line' style={this.getRowStyle()}>
                    <span/>
                    <span/>
                    <span className='underline light fl'>BTW (9%)</span>
                    <span/>
                    <span className='underline end fl'>&euro; {'{btw_totaal}'}</span>
                </div>
                <div className='invoice-line' style={this.getRowStyle()}>
                    <span/>
                    <span/>
                    <span className='total-label fl'
                          style={{fontWeight: font === PdfFont.HELVETICA ? 600 : 500}}>Totaalbedrag incl. BTW</span>
                    <span/>
                    <span className='end fl' style={{
                        fontWeight: font === PdfFont.HELVETICA ? 600 : 500
                    }}>&euro; {'{factuurtotaal}'}</span>
                </div>
            </>
        )
    };

    getTableHeaderLabels = (): [string, string, string, string, string] => {
        const {tableHeaderLabels} = this.props;
        if (tableHeaderLabels) return tableHeaderLabels;
        return ['Omschrijving', 'Aantal', 'Bedrag', 'BTW', 'Subtotaal incl. BTW'];
    }

    render = () => {
        const {dragging, settings} = this.state;
        const {draggable, defaultPosition, width, height, onSaveHistory} = this.props;
        const headLabels = this.getTableHeaderLabels();
        return (
            <WithEvents events={this.event}>
                <PDFDraggable dragging={dragging} draggable={draggable} onDragStart={this.onDragStart}
                              alignment={settings.alignment} elementLabel='Factuurregels' onDragEnd={this.onDragEnd}
                              position={defaultPosition} width={width} height={height} onEdit={this.onClick}
                              onClickBar={this.onClickBar} matchParentSize
                              forwardRef={(ref: HTMLDivElement) => {
                                  this.element = ref
                              }} ref={(ref: PDFDraggable) => {
                    this.draggable = ref
                }}
                              onResized={onSaveHistory} onDragged={onSaveHistory}>
                    <div className='pdf-invoice-table' onClick={this.onClick}>
                        <div className='invoice-line' style={this.getRowStyle(true)}>
                            <span>{headLabels[0]}</span>
                            <span>{headLabels[1]}</span>
                            <span>{headLabels[2]}</span>
                            <span>{headLabels[3]}</span>
                            <span className='end'>{headLabels[4]}</span>
                        </div>
                        {[1, 2].map(this.renderInvoiceLine)}
                        {this.renderTotals()}
                    </div>
                </PDFDraggable>
            </WithEvents>
        )
    }
}